import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import genAiInstance from "./genaiworker";
import { v4 as uuidv4 } from 'uuid'; 



export const useGenAIWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [wsloading, setWsLoading] = useState(false);
  const socketRef = useRef(null);
  const pendingRequestsRef = useRef(new Map());
  const responseCollectorRef = useRef(new Map());
  const reconnectAttemptRef = useRef(0);
  const maxReconnectAttempts = 3;
  const chatIdRef = useRef(uuidv4());
  const getFreshToken = useCallback(() => {
    return localStorage.getItem("userToken");
  }, []);
  const cleanup = useCallback(() => {
    pendingRequestsRef.current.forEach(({ reject, timeoutId }) => {
      if (timeoutId) clearTimeout(timeoutId);
      reject(new Error("WebSocket request cancelled"));
    });
    pendingRequestsRef.current.clear();
    responseCollectorRef.current.clear();
  }, []);

  const connectWebSocket = useCallback(() => {
    try {
      const token = getFreshToken();

      if (!token) {
        return;
      }

//       const getWebSocketUrl = (baseUrl, token) => {
//         const host = baseUrl.replace(/^https?:\/\//, "");
//         return `wss://${host}/ws?chat_id="550e8400-e29b-41d4-a716-446655440000
// "?token=${encodeURIComponent(token)}`;
//       };

     
const getWebSocketUrl = (baseUrl, token) => {
  const host = baseUrl.replace(/^https?:\/\//, "");
  // Fix: Remove the quote marks around chat_id and use chatIdRef.current
  return `wss://${host}/ws?chat_id=${chatIdRef.current}&token=${encodeURIComponent(token)}`;
};
      const wsUrl = getWebSocketUrl(genAiInstance.defaults.baseURL, token);
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = () => {
        setIsConnected(true);
        setWsLoading(false);
        reconnectAttemptRef.current = 0;
      };

      socketRef.current.onmessage = (event) => {
        if (
          event.data === "1::: heartbeat" ||
          event.data.includes("heartbeat")
        ) {
          return;
        }

        try {
          const data = JSON.parse(event.data);
          let matchedRequestId = null;

          if (
            data.requestId &&
            pendingRequestsRef.current.has(data.requestId)
          ) {
            matchedRequestId = data.requestId;
          } else {
            const pendingIds = Array.from(pendingRequestsRef.current.keys());
            if (pendingIds.length > 0) {
              matchedRequestId = pendingIds[pendingIds.length - 1];
            }
          }

          if (
            matchedRequestId &&
            pendingRequestsRef.current.has(matchedRequestId)
          ) {
            const { resolve, timeoutId } =
              pendingRequestsRef.current.get(matchedRequestId);

            if (!responseCollectorRef.current.has(matchedRequestId)) {
              responseCollectorRef.current.set(matchedRequestId, []);
            }

            const responses =
              responseCollectorRef.current.get(matchedRequestId);
            responses.push(data);

            if (timeoutId) clearTimeout(timeoutId);

            const newTimeoutId = setTimeout(() => {
              if (pendingRequestsRef.current.has(matchedRequestId)) {
                const allResponses =
                  responseCollectorRef.current.get(matchedRequestId) || [];
                pendingRequestsRef.current.delete(matchedRequestId);
                responseCollectorRef.current.delete(matchedRequestId);
                resolve(allResponses);
              }
            }, 3000);

            pendingRequestsRef.current.get(matchedRequestId).timeoutId =
              newTimeoutId;
          }
        } catch (error) {
          console.error(" Error parsing WebSocket message:", error);
        }
      };

      socketRef.current.onclose = (event) => {
        setIsConnected(false);
        setWsLoading(false);

        // Auto-reconnect logic for non-manual closures
       if (
          event.code !== 1000 &&
          reconnectAttemptRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptRef.current++;
          setTimeout(() => {
            connectWebSocket();
          }, 2000 * reconnectAttemptRef.current); // Exponential backoff
        } else {
          cleanup();
        }
      };

      socketRef.current.onerror = (error) => {
        setWsLoading(false);
        console.error(" WebSocket error:", error);
        // Don't show toast for auth errors - handle in onclose
        if (reconnectAttemptRef.current >= maxReconnectAttempts) {
          toast.warning("Connection failed. Please refresh the page.");
        }
      };
    } catch (error) {
      console.error(" WebSocket setup error:", error);
      setWsLoading(false);
      toast.warning(
        "Your session has expired due to inactivity. Please refresh or log in again to continue."
      );
    }
  }, [cleanup, getFreshToken]);

  useEffect(() => {
    setWsLoading(true);
    connectWebSocket();

    return () => {
      cleanup();
    };
  }, [connectWebSocket, cleanup]);

  const sendMessage = useCallback((userInput) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current) {
        reject(new Error("WebSocket not initialized"));
        return;
      }

      if (socketRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const requestId =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);

      const mainTimeoutId = setTimeout(() => {
        if (pendingRequestsRef.current.has(requestId)) {
          const allResponses =
            responseCollectorRef.current.get(requestId) || [];
          if (allResponses.length > 0) {
            resolve(allResponses);
          } else {
            reject(new Error("Request timeout - no responses received"));
          }
          pendingRequestsRef.current.delete(requestId);
          responseCollectorRef.current.delete(requestId);
        }
      }, 90000);

      pendingRequestsRef.current.set(requestId, {
        resolve,
        reject,
        timeoutId: mainTimeoutId,
      });

      try {
        socketRef.current.send(userInput);
      } catch (sendError) {
        pendingRequestsRef.current.delete(requestId);
        responseCollectorRef.current.delete(requestId);
        clearTimeout(mainTimeoutId);
        reject(new Error(`Failed to send message: ${sendError.message}`));
      }
    });
  }, []);

  const fetchProductCountry = useCallback(
    async (userInput) => {
      try {
        const responses = await sendMessage(userInput);

        return responses;
      } catch (error) {
        throw error;
      }
    },
    [sendMessage]
  );

  const reconnect = useCallback(() => {
    setWsLoading(true);
    cleanup();
    if (socketRef.current) {
      socketRef.current.close();
    }
    connectWebSocket();
  }, [connectWebSocket, cleanup]);

  const disconnect = useCallback(() => {
    setWsLoading(false);
    cleanup();
    if (socketRef.current) {
      socketRef.current.close(1000, "Manual disconnect");
    }
  }, [cleanup]);

  return {
    isConnected,
    sendMessage,
    fetchProductCountry,
    reconnect,
    disconnect,
    wsloading,
  };
};
